import { useGetAttachment } from '@services/chat/queries/useGetAttachment'
import { useProfile } from '@services/users/queries/useProfile'
import dayjs from 'dayjs'
import { Paperclip, User as UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MessageDetail } from 'types/api'

import { cn } from '@/lib/utils'

import style from '../ChatComponents.module.scss'

type Attachment = NonNullable<MessageDetail['attachments']>[number]

function MessageAttachment({ attachment }: { attachment: Attachment }) {
  const { data: blob } = useGetAttachment({ id: attachment.id })

  const handleDownload = () => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.name ?? 'attachment'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  if (blob && attachment.mimetype?.includes('image')) {
    return (
      <img
        src={URL.createObjectURL(blob)}
        alt={attachment.name ?? ''}
        className="mt-1 w-[150px] cursor-pointer rounded-2xl object-cover"
        onClick={handleDownload}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={handleDownload}
      className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-left text-sm font-semibold text-ink"
    >
      <Paperclip className="h-4 w-4" />
      {attachment.name}
    </button>
  )
}

export function ChatMessage({ message }: { message: MessageDetail }) {
  const { data: profile } = useProfile(true)
  const { t } = useTranslation('chat')
  const isOwn = message.type === profile?.role

  return (
    <div className={cn('flex gap-2', isOwn ? style.user : style.admin)}>
      {!isOwn && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink/[0.05] text-ink">
          <UserIcon className="h-5 w-5" />
        </span>
      )}
      <div
        className={cn(
          'flex flex-1 flex-col gap-1',
          isOwn ? 'items-end' : 'items-start'
        )}
      >
        <div className={style['chat-message']} style={{ wordBreak: 'break-word' }}>
          {message.type !== 'USER' && (
            <span className={style['chat-item-title']} style={{ fontSize: 14 }}>
              Admin
            </span>
          )}
          <span
            className={cn(
              style['chat-item-text'],
              // Messages carry their structure in blank lines and hard wraps
              // (see WELCOME_MESSAGES on the backend), which collapse without this.
              'whitespace-pre-wrap'
            )}
          >
            {message.message}
          </span>
          {message.attachments?.map((attachment) => (
            <MessageAttachment key={attachment.id} attachment={attachment} />
          ))}
        </div>
        <div className={cn('flex justify-between gap-2 self-stretch', style['message-status'])}>
          <span className={style['message-time']}>
            {dayjs(message.created_at).format('DD.MM.YYYY HH:mm')}
          </span>
          {message.is_read && isOwn && (
            <span className={style['message-time']}>
              {t('form.common.seen')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
